/**
 * allTimeAggregates — countable all-time facts for the Scores tab tile grid.
 *
 * HARD RULE (owner): every score is a fact someone could recount by hand —
 * a time, a count, a distance, a date. No indices, no 0–100, no weighting.
 *
 * Data reality (investigated 2026-07-08): lap-level facts (fastest length,
 * lengths before first rest, fly runs) exist only transiently on a
 * DerivedWorkout — the persisted per-swim cache (strokeStats.v2) stores
 * per-stroke AVERAGES, not per-lap facts. So this module owns a sibling
 * cache, `swimset.swimFacts.v1`, written from the exact same two paths that
 * feed strokeStats (Prototypes' live derive + setsDiscovered's background
 * scan) so no NEW fetch or re-derivation is ever triggered by the Scores tab
 * itself. History backfills through the scan's existing retryable
 * `hasDerived` seam (6fdd7f6) — swims already counted into the sets ledger
 * but missing swimFacts get refetched once, in the background, without being
 * re-counted.
 *
 * Coverage honesty: aggregates here run over WHATEVER the cache holds. The
 * Scores tab must label partial coverage ("across N swims"), never present a
 * partial scan as an all-time truth. `factsCoverage` gives it the numbers.
 *
 * v2 (2026-09-01, spec item 3 "the speed thread"): each entry also carries
 * the swim's named SETS (`sets`) — whole-set time, meters, and per-stroke
 * n/mean/best over counted laps — so the Scores tab can show first/fastest/
 * last for The Rockalita etc. and the Club card's own-side numbers come from
 * the same extractor (showcase-no-fork). Migration: the v2 key is new; a v1
 * blob is carried over VERBATIM with `sets: null` so the tile grid stays
 * populated on the first v2 boot, and `hasSwimFacts` reports those carried
 * entries as NOT derived so the history scan's existing hasDerived retry
 * seam refetches each one ONCE in the background (no re-count, no tab-load
 * re-derivation). `setsCoverage` gives the Your-sets section its honest
 * "Counted from N of M swims so far" numbers while that backfill runs.
 *
 * v3 (2026-09-01, "stroke insights" + the record floor): each entry also
 * carries a per-stroke block (`strokes`: n / median / best seconds, mean and
 * best strokes-per-length) for the "Your strokes · per 25" section, and every
 * RECORD (fastest length, set bests, stroke bests) now passes the
 * recordEligibility floor so a split-length watch glitch (9.4 s breaststroke)
 * can never be a personal best. Counts and averages are untouched —
 * "everything counts." Migration mirrors v1→v2: a v2 blob is carried over
 * VERBATIM with `strokes: null` (sets kept, so Your-sets stays populated), a
 * v1 blob with both `sets` and `strokes` null; `hasSwimFacts` reports either
 * as not derived so the scan's hasDerived seam re-extracts each once.
 */
import type { WorkoutSummary } from "@contract/types";
import type { Lap } from "./laps";
import type { RestInterval } from "./restIntervals";
import type { SetGroup } from "./setGroups";
import {
  isRecordEligible,
  medianDurationByStroke,
  medianWithLaps,
  recordFloorSeconds,
  weightedMedian,
  type MedianByStroke,
} from "./recordEligibility";

/** Strokes that can hold the fastest-length record. Unknown, Kickboard, Mixed cannot. */
const RECORD_STROKES = new Set(["Freestyle", "Backstroke", "Breaststroke", "Butterfly"]);

export const SWIM_FACTS_CACHE_KEY = "swimset.swimFacts.v5";
export const SWIM_FACTS_CACHE_KEY_V4 = "swimset.swimFacts.v4";
export const SWIM_FACTS_CACHE_KEY_V3 = "swimset.swimFacts.v3";
export const SWIM_FACTS_CACHE_KEY_V2 = "swimset.swimFacts.v2";
export const SWIM_FACTS_CACHE_KEY_V1 = "swimset.swimFacts.v1";
/**
 * Bumped 3→4 on 2026-09-09 for the "a known set trumps Warmup/Cooldown" label
 * rule (setGroups): every `sets[].setId` extracted under v3 can be wrong.
 *
 * Bumped 4→5 on 2026-09-11 for `sets[].blocks` — the set's lengths in SWIM
 * ORDER (Scott: "on the sets you should show what the set actually is visually
 * instead of having to click it to see the colors"). `perStroke` is a
 * per-stroke AGGREGATE, so a set drawn from it reads fly-fly-breast-breast-free
 * — an order the set never swam, and one its own name contradicts.
 *
 * Both are CACHE MIGRATIONS for the same reason: these facts are extracted ONCE
 * per swim and never revisited (scanHistory's `hasDerived` seam skips a swim
 * that already has facts). The older entries are carried over with `sets: null`
 * — the module's existing "not yet re-extracted" state — so `hasSwimFacts`
 * reports false and the background scan re-derives each swim once, while the
 * tiles that do NOT depend on sets keep reading and a swim older than
 * listWorkouts' 100-swim window keeps its facts instead of vanishing from the
 * Scores tab.
 */
export const SWIM_FACTS_VERSION = 5;

/** Per-stroke lap stats inside one set. n / meanS cover every counted lap
 *  (the set's story); bestS only record-eligible laps — null when none was. */
export interface SwimFactsStroke {
  n: number;
  meanS: number;
  bestS: number | null;
}

/** Per-stroke summary of one whole swim (v3), record-eligible laps only.
 *  Seconds are per length in THIS swim's pool (see entry.poolLengthMeters). */
export interface SwimFactsStrokeSummary {
  n: number;
  medianS: number;
  bestS: number;
  /** Mean strokes per length over laps that carried a stroke count; null if
   *  none did. No "fewest strokes" is kept: a stroke count is not a
   *  standalone record (a 4-stroke butterfly length is a glitch). */
  meanSpl: number | null;
  /** Rest taken before lengths of this stroke (seconds): the quartiles of
   *  every inter-lap gap ≥ REP_REST_MIN_MS that preceded one of its eligible
   *  lengths. null when fewer than 2 such rests, or the source had no lap
   *  start times. */
  restS: { p25: number; p75: number; n: number } | null;
}

/** An inter-lap gap this long or longer is rest before the next length. A
 *  turn, a breath at the wall, or a slow IM changeover is 2–8 s and is NOT
 *  rest (at 3 s the real history read "reps on 3–7s rest" — wall touches).
 *  Deliberately below REST_GAP_MS (25 s, the STRUCTURAL rest that splits
 *  sets) — "reps on 15 s rest" is rest too. */
export const REP_REST_MIN_MS = 10_000;

/** A run of consecutive same-stroke lengths inside one set, in SWIM ORDER —
 *  the same run rule the share card's `blocksFor` draws its chips from. Every
 *  member length counts, excluded or not: a set is a structural unit, and the
 *  picture is of what was swum, not of what was counted. */
export interface SwimFactsSetBlock {
  stroke: string;
  /** Lengths in this run. Sums to the set's `lapCount` across all blocks. */
  n: number;
}

/** One named set inside one swim (setId non-null, not a bookend). */
export interface SwimFactsSet {
  setId: string;
  /** The set's own display label at extraction time ("The Rockalita"). */
  label: string;
  /** Sum of member-lap meters (every lap — the set is a structural unit). */
  meters: number;
  /** Whole-set time, every lap — the same number the Club set-time row shows. */
  durationSeconds: number;
  /** Every member length, excluded or not. */
  lapCount: number;
  /** Set start, epoch ms — orders two of the same set inside one swim. */
  startMs: number;
  poolLengthMeters: number;
  /** Keyed by effective stroke; excluded laps dropped. Empty when every lap was excluded. */
  perStroke: Record<string, SwimFactsStroke>;
  /** The set's lengths in swim order, run-length encoded (v5). What the Sets
   *  tab draws: `perStroke` can only say WHICH strokes, never in what order. */
  blocks: SwimFactsSetBlock[];
  /** Counted laps that failed the record floor (split-length glitches). The
   *  occurrence still counts and still draws; only the "fastest" tile skips it. */
  ineligibleLaps: number;
}

/** Per-swim countable facts. Lap-dependent fields are null when the workout
 *  had no per-length lap events (poolLengthMeters unresolvable) — a synthetic
 *  whole-workout lap is not a length and must never fake a record. */
export interface SwimFactsEntry {
  swimUuid: string;
  /** ISO date "YYYY-MM-DD" */
  swimDate: string;
  fastestLengthSeconds: number | null;
  /** Effective stroke of the fastest length (corrections applied upstream). */
  fastestLengthStroke: string | null;
  /** Counted lengths swum before the first rest gap; whole swim if no rest. */
  lengthsBeforeFirstRest: number | null;
  /** Counted (non-excluded) lengths in this swim. */
  lengthCount: number | null;
  /** Longest run of consecutive Butterfly lengths. 0 = lap data but no fly. */
  longestFlyRun: number | null;
  /** Named sets in this swim (v2). Empty = lap data but no named set. null =
   *  carried over from a v1 blob, not yet re-extracted (see module doc). */
  sets: SwimFactsSet[] | null;
  /** Pool length (v3); null for open water / no lap events. Tells readers
   *  which pool the `strokes` seconds belong to. */
  poolLengthMeters: number | null;
  /** Per-stroke summary (v3) over record-eligible laps. Empty = no lap data.
   *  null = carried over from a v1/v2 blob, not yet re-extracted. */
  strokes: Record<string, SwimFactsStrokeSummary> | null;
}

export interface SwimFactsCache {
  version: number;
  entries: Record<string, SwimFactsEntry>;
}

/** Structural subset of DerivedWorkout that extractSwimFacts reads. Keeps
 *  tests light — no 15-field DerivedWorkout fixtures needed. */
export interface SwimFactsSource {
  laps: SwimFactsLapSource[];
  poolLengthMeters: number | null;
  restIntervals: Pick<RestInterval, "startMs">[];
  /** Optional so the older lap-only fixtures still type-check; absent ⇒ sets: []. */
  setGroups?: SwimFactsSetSource[];
}

/** Structural subset of Lap the extractor reads. strokeCount / lengthMeters
 *  are optional (v3) so the lap-only fixtures still type-check; absent ⇒
 *  no stroke count, the swim's pool length. */
export type SwimFactsLapSource = Pick<Lap, "durationSeconds" | "strokeStyle" | "effectiveExcluded" | "endMs"> &
  Partial<Pick<Lap, "strokeCount" | "lengthMeters" | "startMs">>;

/** Structural subset of SetGroup that the sets extractor reads. */
export interface SwimFactsSetSource
  extends Pick<SetGroup, "setId" | "label" | "meters" | "durationSeconds" | "startMs"> {
  laps: (Pick<Lap, "durationSeconds" | "strokeStyle" | "effectiveExcluded"> &
    Partial<Pick<Lap, "strokeCount" | "lengthMeters">>)[];
}

/** Warmup / Cooldown are bookends, never "a set you've swum" (mirrors
 *  setsDiscovered.BOOKEND_SET_IDS; duplicated as a literal to keep this module
 *  free of the svelte-store import graph). */
const BOOKEND_SET_IDS: ReadonlySet<string> = new Set(["warmup", "cooldown"]);

// ─── Persistence (mirrors strokeStats.ts exactly) ───────────────────────────

function emptyCache(): SwimFactsCache {
  return { version: SWIM_FACTS_VERSION, entries: {} };
}

/** Read an older blob's entries; null when absent, wrong version, or corrupt. */
function readOlderBlob(key: string, version: number): Record<string, unknown> | null {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { version?: unknown; entries?: unknown };
    if (parsed.version !== version || typeof parsed.entries !== "object" || parsed.entries === null) {
      return null;
    }
    return parsed.entries as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Carry an older blob's entries over VERBATIM, filling the fields that
 * version never had with null. Never throws. v2 first (it has sets), then v1.
 * The tiles (fastest length, before-first-rest, totals, fly) and, for v2,
 * the Your-sets section read these unchanged; the Your-strokes section skips
 * them until the scan re-extracts each swim.
 */
function salvageOlderEntries(): Record<string, SwimFactsEntry> | null {
  // v4: everything survives EXCEPT `sets`, nulled for the same reason v3's was
  // — a v4 set has no `blocks`, so its picture would have to be invented from
  // the per-stroke aggregate. Nulling makes hasSwimFacts false, and the scan
  // re-derives each swim once with real swim order.
  const v4 = readOlderBlob(SWIM_FACTS_CACHE_KEY_V4, 4);
  if (v4) {
    const out: Record<string, SwimFactsEntry> = {};
    for (const [uuid, raw] of Object.entries(v4)) {
      out[uuid] = { ...(raw as SwimFactsEntry), sets: null };
    }
    return out;
  }
  // v3: everything survives EXCEPT `sets`, which is nulled on purpose — the
  // 3→4 bump exists because those setIds may name a bookend where the swim
  // actually holds a signature set. Nulling marks them "not yet re-extracted",
  // which is exactly what makes hasSwimFacts false and the rescan re-derive.
  const v3 = readOlderBlob(SWIM_FACTS_CACHE_KEY_V3, 3);
  if (v3) {
    const out: Record<string, SwimFactsEntry> = {};
    for (const [uuid, raw] of Object.entries(v3)) {
      out[uuid] = { ...(raw as SwimFactsEntry), sets: null };
    }
    return out;
  }
  const v2 = readOlderBlob(SWIM_FACTS_CACHE_KEY_V2, 2);
  if (v2) {
    const out: Record<string, SwimFactsEntry> = {};
    for (const [uuid, raw] of Object.entries(v2)) {
      const e = raw as Omit<SwimFactsEntry, "poolLengthMeters" | "strokes">;
      // `blocks: []` rather than a guess: a v2 set knows its strokes but not
      // their order, and the row draws no picture until the rescan (which this
      // carry-over's `strokes: null` already asks for) replaces it.
      const sets =
        e.sets === null
          ? null
          : e.sets.map((s) => ({ ...s, ineligibleLaps: s.ineligibleLaps ?? 0, blocks: s.blocks ?? [] }));
      out[uuid] = { ...e, sets, poolLengthMeters: sets?.[0]?.poolLengthMeters ?? null, strokes: null };
    }
    return out;
  }
  const v1 = readOlderBlob(SWIM_FACTS_CACHE_KEY_V1, 1);
  if (v1) {
    const out: Record<string, SwimFactsEntry> = {};
    for (const [uuid, raw] of Object.entries(v1)) {
      const e = raw as Omit<SwimFactsEntry, "sets" | "poolLengthMeters" | "strokes">;
      out[uuid] = { ...e, sets: null, poolLengthMeters: null, strokes: null };
    }
    return out;
  }
  return null;
}

export function loadSwimFactsCache(): SwimFactsCache {
  try {
    const raw =
      typeof localStorage === "undefined" ? null : localStorage.getItem(SWIM_FACTS_CACHE_KEY);
    if (!raw) return { version: SWIM_FACTS_VERSION, entries: salvageOlderEntries() ?? {} };
    const parsed = JSON.parse(raw) as Partial<SwimFactsCache>;
    if (
      parsed.version !== SWIM_FACTS_VERSION ||
      typeof parsed.entries !== "object" ||
      parsed.entries === null
    ) {
      return emptyCache();
    }
    return {
      version: SWIM_FACTS_VERSION,
      entries: parsed.entries as Record<string, SwimFactsEntry>,
    };
  } catch {
    return emptyCache();
  }
}

export function saveSwimFactsCache(cache: SwimFactsCache): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(SWIM_FACTS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / private-mode — degrades to in-session only (same known debt as strokeStats) */
  }
}

/** All cached per-swim facts as a flat array. Aggregates below consume this. */
export function getAllSwimFacts(): SwimFactsEntry[] {
  return Object.values(loadSwimFactsCache().entries);
}

/** Has this swim's facts side-write landed AT THE CURRENT VERSION? A v1/v2
 *  carry-over (sets or strokes null) answers false so the scan's hasDerived
 *  retry seam (setsDiscovered.scanHistory) refetches it once — the designed
 *  backfill. */
export function hasSwimFacts(swimUuid: string): boolean {
  const e = loadSwimFactsCache().entries[swimUuid];
  return e !== undefined && e.sets !== null && e.strokes !== null;
}

// ─── Extraction ──────────────────────────────────────────────────────────────

/**
 * Pure. Distill one derived workout into its countable facts.
 *
 * - Excluded laps (glitches, wall-laps, user-marked-out) never set a record
 *   and never count as lengths — same filter every aggregate consumer uses.
 * - No pool length ⇒ no per-length lap events ⇒ all lap facts null (the
 *   single synthetic whole-workout lap is not a length).
 * - Fly runs: consecutive counted Butterfly lengths. An EXCLUDED lap in the
 *   middle (a glitch) does not break the run; any other stroke does.
 * - Records (fastest length, set bests, the per-stroke block) additionally
 *   pass the recordEligibility floor, measured against `priorMedians` (the
 *   cache's per-stroke medians) with this swim's own laps folded in. Counts
 *   and averages never filter on it.
 */
export function extractSwimFacts(
  src: SwimFactsSource,
  swimUuid: string,
  swimDate: string,
  priorMedians: MedianByStroke = {},
): SwimFactsEntry {
  const base = { swimUuid, swimDate: swimDate.slice(0, 10), poolLengthMeters: src.poolLengthMeters };
  if (src.poolLengthMeters === null || src.laps.length === 0) {
    return {
      ...base,
      fastestLengthSeconds: null,
      fastestLengthStroke: null,
      lengthsBeforeFirstRest: null,
      lengthCount: null,
      longestFlyRun: null,
      sets: [],
      strokes: {},
    };
  }

  const pool = src.poolLengthMeters;
  const withLength = <T extends { lengthMeters?: number | null }>(l: T): T & { lengthMeters: number } => ({
    ...l,
    lengthMeters: l.lengthMeters ?? pool,
  });
  const laps = src.laps.map(withLength);
  const medians = medianWithLaps(priorMedians, laps);
  const counted = laps.filter((l) => !l.effectiveExcluded);
  const eligible = counted.filter((l) => isRecordEligible(l, medians));

  let fastestLengthSeconds: number | null = null;
  let fastestLengthStroke: string | null = null;
  // A record needs a named stroke: an "Unknown" (drill) or Kickboard length can be the
  // fastest thing in a swim and still say nothing about swimming a stroke fast.
  for (const lap of eligible) {
    if (!RECORD_STROKES.has(lap.strokeStyle)) continue;
    if (fastestLengthSeconds === null || lap.durationSeconds < fastestLengthSeconds) {
      fastestLengthSeconds = lap.durationSeconds;
      fastestLengthStroke = lap.strokeStyle;
    }
  }

  const firstRest = src.restIntervals[0];
  const lengthsBeforeFirstRest = firstRest
    ? counted.filter((l) => l.endMs <= firstRest.startMs).length
    : counted.length;

  let longestFlyRun = 0;
  let run = 0;
  for (const lap of src.laps) {
    if (lap.effectiveExcluded) continue; // a glitch never breaks a real run
    if (lap.strokeStyle === "Butterfly") {
      run++;
      if (run > longestFlyRun) longestFlyRun = run;
    } else {
      run = 0;
    }
  }

  return {
    ...base,
    fastestLengthSeconds,
    fastestLengthStroke,
    lengthsBeforeFirstRest,
    lengthCount: counted.length,
    longestFlyRun,
    sets: extractSets((src.setGroups ?? []).map((g) => ({ ...g, laps: g.laps.map(withLength) })), pool, medians),
    strokes: extractStrokes(laps, medians),
  };
}

/** Pure. Consecutive same-stroke lengths → one block each, in swim order —
 *  the share card's `blocksFor` rule (shareCard/build.ts) with only the two
 *  fields a cached picture needs. Every length counts, excluded or not. */
export function strokeRuns(strokes: readonly string[]): SwimFactsSetBlock[] {
  const out: SwimFactsSetBlock[] = [];
  for (const stroke of strokes) {
    const last = out[out.length - 1];
    if (last && last.stroke === stroke) last.n += 1;
    else out.push({ stroke, n: 1 });
  }
  return out;
}

/** Pure. Named, non-bookend sets: n / mean over counted laps, best over
 *  record-eligible laps, plus how many counted laps failed the floor. */
function extractSets(
  groups: SwimFactsSetSource[],
  poolLengthMeters: number,
  medians: MedianByStroke,
): SwimFactsSet[] {
  const out: SwimFactsSet[] = [];
  for (const g of groups) {
    if (g.setId === null || BOOKEND_SET_IDS.has(g.setId)) continue;
    const sums = new Map<string, { total: number; n: number; best: number | null }>();
    let ineligibleLaps = 0;
    for (const lap of g.laps) {
      if (lap.effectiveExcluded || !(lap.durationSeconds > 0)) continue;
      const cur = sums.get(lap.strokeStyle) ?? { total: 0, n: 0, best: null };
      cur.total += lap.durationSeconds;
      cur.n += 1;
      if (isRecordEligible(lap, medians)) {
        cur.best = cur.best === null ? lap.durationSeconds : Math.min(cur.best, lap.durationSeconds);
      } else {
        ineligibleLaps++;
      }
      sums.set(lap.strokeStyle, cur);
    }
    const perStroke: Record<string, SwimFactsStroke> = {};
    for (const [stroke, { total, n, best }] of sums) {
      perStroke[stroke] = { n, meanS: total / n, bestS: best };
    }
    out.push({
      blocks: strokeRuns(g.laps.map((l) => l.strokeStyle)),
      setId: g.setId,
      label: g.label,
      meters: Math.round(g.meters),
      durationSeconds: g.durationSeconds,
      lapCount: g.laps.length,
      startMs: g.startMs,
      poolLengthMeters,
      perStroke,
      ineligibleLaps,
    });
  }
  return out;
}

/** Lower quartile / upper quartile by rank (n ≥ 2). */
function quartiles(xs: number[]): { p25: number; p75: number } {
  const s = [...xs].sort((a, b) => a - b);
  return { p25: s[Math.floor((s.length - 1) * 0.25)], p75: s[Math.floor((s.length - 1) * 0.75)] };
}

/** Pure. One summary per stroke over the swim's record-eligible laps. `laps`
 *  is the whole swim in order — the rest before a length is measured from
 *  the previous lap of ANY stroke. */
function extractStrokes(
  laps: (SwimFactsLapSource & { lengthMeters: number })[],
  medians: MedianByStroke,
): Record<string, SwimFactsStrokeSummary> {
  const by = new Map<string, { s: number[]; spl: number[]; rest: number[] }>();
  for (let i = 0; i < laps.length; i++) {
    const lap = laps[i];
    if (lap.effectiveExcluded || !isRecordEligible(lap, medians)) continue;
    const cur = by.get(lap.strokeStyle) ?? { s: [], spl: [], rest: [] };
    cur.s.push(lap.durationSeconds);
    if (lap.strokeCount != null) cur.spl.push(lap.strokeCount);
    const prev = laps[i - 1];
    if (prev && lap.startMs !== undefined) {
      const gapMs = lap.startMs - prev.endMs;
      if (gapMs >= REP_REST_MIN_MS) cur.rest.push(gapMs / 1000);
    }
    by.set(lap.strokeStyle, cur);
  }
  const out: Record<string, SwimFactsStrokeSummary> = {};
  for (const [stroke, { s, spl, rest }] of by) {
    out[stroke] = {
      n: s.length,
      medianS: weightedMedian(s.map((v) => ({ v, w: 1 })))!,
      bestS: Math.min(...s),
      meanSpl: spl.length === 0 ? null : spl.reduce((a, b) => a + b, 0) / spl.length,
      restS: rest.length < 2 ? null : { ...quartiles(rest), n: rest.length },
    };
  }
  return out;
}

/** UPSERT one swim's facts into the cache. Idempotent — same uuid overwrites
 *  (last write wins), so the live re-derive after a stroke correction keeps
 *  the facts honest. The record floor is measured against the medians of
 *  every OTHER swim already in the cache (plus this swim's own laps). Never
 *  throws. */
export function recordSwimFacts(
  src: SwimFactsSource,
  swimUuid: string,
  swimDate: string,
): void {
  const cache = loadSwimFactsCache();
  const others = Object.values(cache.entries).filter((e) => e.swimUuid !== swimUuid);
  cache.entries[swimUuid] = extractSwimFacts(src, swimUuid, swimDate, medianDurationByStroke(others));
  saveSwimFactsCache(cache);
}

// ─── Aggregates ──────────────────────────────────────────────────────────────
// Tie rule everywhere: the EARLIEST swim keeps the record (the date a record
// shows is the date it was first set).

export interface FastestLength {
  seconds: number;
  stroke: string | null;
  /** "YYYY-MM-DD" */
  swimDate: string;
}

/**
 * The fastest record-eligible 25 m length on record. 25 m pool swims only:
 * a 20 m length is faster by geometry, not by swimming, and mixing pools on
 * one tile let a 14.0 s half-length from the 20 m pool win it (2026-09-01).
 * A carry-over whose pool is unknown (null) is measured as a 25 so the tile
 * does not blank on the first v3 boot; the scan's re-extract settles it.
 * Each entry's fastest already passed the floor at extraction; it is
 * re-checked here against the MATURE all-history median (`medians`),
 * because a swim extracted early in a scan only had its own laps to measure
 * against.
 */
export function fastestLengthEver(
  entries: SwimFactsEntry[],
  medians: MedianByStroke = medianDurationByStroke(entries),
): FastestLength | null {
  let best: FastestLength | null = null;
  for (const e of entries) {
    if (e.fastestLengthSeconds === null) continue;
    // Records are 25 m lengths only: a 20 m-pool length is faster by geometry, not by swimming.
    if (e.poolLengthMeters !== null && e.poolLengthMeters !== undefined && e.poolLengthMeters !== 25) continue;
    if (
      e.fastestLengthStroke !== null &&
      e.fastestLengthSeconds < recordFloorSeconds(e.fastestLengthStroke, e.poolLengthMeters ?? 25, medians)
    ) {
      continue;
    }
    if (
      best === null ||
      e.fastestLengthSeconds < best.seconds ||
      (e.fastestLengthSeconds === best.seconds && e.swimDate < best.swimDate)
    ) {
      best = {
        seconds: e.fastestLengthSeconds,
        stroke: e.fastestLengthStroke,
        swimDate: e.swimDate,
      };
    }
  }
  return best;
}

export interface CountRecord {
  count: number;
  /** "YYYY-MM-DD" */
  swimDate: string;
}

export function mostLengthsBeforeFirstRest(entries: SwimFactsEntry[]): CountRecord | null {
  let best: CountRecord | null = null;
  for (const e of entries) {
    if (e.lengthsBeforeFirstRest === null) continue;
    if (
      best === null ||
      e.lengthsBeforeFirstRest > best.count ||
      (e.lengthsBeforeFirstRest === best.count && e.swimDate < best.swimDate)
    ) {
      best = { count: e.lengthsBeforeFirstRest, swimDate: e.swimDate };
    }
  }
  return best;
}

export interface FarthestSwim {
  meters: number;
  /** Full ISO timestamp from the workout summary. */
  startDate: string;
}

/** Reads the plain workout list (no lap data needed) — covers every swim the
 *  bridge returns, independent of the facts cache. */
export function farthestSwim(workouts: WorkoutSummary[]): FarthestSwim | null {
  let best: FarthestSwim | null = null;
  for (const w of workouts) {
    const meters = w.totalDistance?.value ?? 0;
    if (meters <= 0) continue;
    if (
      best === null ||
      meters > best.meters ||
      (meters === best.meters && new Date(w.startDate).getTime() < new Date(best.startDate).getTime())
    ) {
      best = { meters, startDate: w.startDate };
    }
  }
  return best;
}

export interface TotalLengths {
  total: number;
  /** How many swims contributed lap data — the honest "across N swims" N. */
  swimCount: number;
}

export function totalLengths(entries: SwimFactsEntry[]): TotalLengths {
  let total = 0;
  let swimCount = 0;
  for (const e of entries) {
    if (e.lengthCount === null) continue;
    total += e.lengthCount;
    swimCount++;
  }
  return { total, swimCount };
}

export interface FlyStretch {
  count: number;
  /** null when count is 0 (no fly swum yet — no date to point at). */
  swimDate: string | null;
}

export function longestFlyStretch(entries: SwimFactsEntry[]): FlyStretch | null {
  let any = false;
  let best: { count: number; swimDate: string } | null = null;
  for (const e of entries) {
    if (e.longestFlyRun === null) continue;
    any = true;
    if (e.longestFlyRun === 0) continue;
    if (
      best === null ||
      e.longestFlyRun > best.count ||
      (e.longestFlyRun === best.count && e.swimDate < best.swimDate)
    ) {
      best = { count: e.longestFlyRun, swimDate: e.swimDate };
    }
  }
  if (!any) return null;
  return best ?? { count: 0, swimDate: null };
}

export interface FactsCoverage {
  /** Swims with lap facts in the cache. */
  counted: number;
  /** Total swims known to the app (clamped ≥ counted so "N of M" never lies). */
  total: number;
  /** True when the lap-fact tiles cover every known swim. */
  complete: boolean;
}

export function factsCoverage(entries: SwimFactsEntry[], totalSwims: number): FactsCoverage {
  const counted = entries.filter((e) => e.lengthCount !== null).length;
  const total = Math.max(counted, totalSwims);
  return { counted, total, complete: counted >= total };
}

/** Coverage for the Your-sets section: only entries whose sets were extracted
 *  (v1 carry-overs with sets: null are still waiting on the backfill). */
export function setsCoverage(entries: SwimFactsEntry[], totalSwims: number): FactsCoverage {
  const counted = entries.filter((e) => e.sets !== null).length;
  const total = Math.max(counted, totalSwims);
  return { counted, total, complete: counted >= total };
}

/** Coverage for the Your-strokes section: only entries whose per-stroke
 *  block was extracted (v1/v2 carry-overs with strokes: null are waiting). */
export function strokesCoverage(entries: SwimFactsEntry[], totalSwims: number): FactsCoverage {
  const counted = entries.filter((e) => e.strokes !== null).length;
  const total = Math.max(counted, totalSwims);
  return { counted, total, complete: counted >= total };
}
